import { Injectable } from '@angular/core';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, map, mergeMap, switchMap, timeout } from 'rxjs/operators';
import { AppAdapter } from 'src/app/adapters/app-info/app.adapter';
import { MSAppInfo } from 'src/app/adapters/app-info/utils/app.utils';
import { DeviceAdapter } from 'src/app/adapters/device/device.adapter';
import { MSDeviceInfo } from 'src/app/adapters/device/utils/device.utils';
import { EnvironmentAdapter } from 'src/app/adapters/environment/environment.adapter';
import { MSHttpEvent } from 'src/app/adapters/http/utils/http.utils';
import { Environment } from 'src/environments/environment.utils';
import { FeatureFlagsUseCase } from '../../feature-flags/feature-flags';
import { TypeTransformerUseCase } from '../../type-transformer/type-transformer.use-case';
import { MSApiHttpUseCase } from '../http/msapi-http.use-case';
import { ConfigEvent, GetConfigHttpResponse } from './utils/get-config.utils';

/* The ConfigUseCase class is responsible for retrieving configuration data from the MS API.*/
@Injectable({
  providedIn: 'root'
})
export class GetConfigUseCase {

  private argumentsObservable = forkJoin([
    this.environmentAdapter.getEnvironment(),
    this.deviceAdapter.getDeviceInfo(),
    this.appAdapter.getAppInfo()
  ]);

  private execuateCallSignal = new Subject<string>();
  public eventStream = this.execuateCallSignal.pipe(
    mergeMap((authCode) =>
      this.argumentsObservable.pipe(
        map(
          ([environment, deviceInfo, appInfo]) =>
            [authCode, environment, deviceInfo, appInfo] as [string, Environment, MSDeviceInfo, MSAppInfo]
        )
      )
    ),
    switchMap(([authCode, environment, deviceInfo, appInfo]) => this.callEndpoint(authCode, environment, deviceInfo, appInfo)),
    distinctUntilChanged((previous, current) => previous instanceof ConfigEvent.Loading && current instanceof ConfigEvent.Loading),
  );

  constructor(
    private api: MSApiHttpUseCase,
    private featureFlagManager: FeatureFlagsUseCase,
    private environmentAdapter: EnvironmentAdapter,
    private transformer: TypeTransformerUseCase,
    private deviceAdapter: DeviceAdapter,
    private appAdapter: AppAdapter
  ) {}

  public execute(authCode: string) {
    this.execuateCallSignal.next(authCode);
  }

  private callEndpoint(
    authCode: string,
    environment: Environment,
    deviceInfo: MSDeviceInfo,
    appInfo: MSAppInfo
  ): Observable<ConfigEvent.Spec> {
    const configObservable = this.api
      .get('get_config', {
        authCode,
        platform: deviceInfo.platform,
        appVersion: appInfo.version
      })
      .pipe(
        timeout(environment.distanceConfig.httpRequestTimeout),
        mergeMap((event) => this.mapMSHttpEventToGetConfigEvent(event)),
        catchError((error) => of(this.mapMSHttpEventToGetConfigEvent(error)))
      );

    return configObservable;
  }

  private mapMSHttpEventToGetConfigEvent(event: MSHttpEvent.Spec): Observable<ConfigEvent.Spec> {
    if (event instanceof MSHttpEvent.Success) {
      const data = this.transformer.transformToType(GetConfigHttpResponse, event.data);

      return this.featureFlagManager
        .set(data.featureFlags)
        .pipe(
          map(() => new ConfigEvent.Success(data.configuration))
        );
    } else if (event instanceof MSHttpEvent.Error) {
      if (event.status === 0) {
        const state = new ConfigEvent.ConnectionError();
        return of(state);
      }

      const state = new ConfigEvent.Error();
      return of(state);
    } else if (event instanceof MSHttpEvent.Loading) {
      const state = new ConfigEvent.Loading();
      return of(state);
    }
  }
}
